import { supabase } from "./supabase";
import type { Escrita } from "./conteudo";

// As ocupações do MEI: a lista de referência que responde "posso ser MEI com
// esta atividade?" (000086).
//
// A lista inteira vem de uma vez (502 linhas) e o filtro é local: busca por
// tecla é interação de tela, e uma chamada de rede por letra digitada seria
// pagar viagem pelo que o navegador faz de graça. Quem decide se pode ler é
// `mei_ocupacoes_lista` — que pergunta a `conteudo_liberado('mei-pro')`, ou
// seja a `abrir_card`. Este módulo não decide acesso: ele transporta.

export type Ocupacao = {
  id: string;
  vedada: boolean;
  atividade: string;
  cnae: string | null;
  descricao: string | null;
  icms: boolean | null;
  iss: boolean | null;
  /** Só nas vedadas: a norma que tirou a atividade do MEI. */
  exclusao: string | null;
};

function falha(mensagem: string | undefined): { ok: false; mensagem: string } {
  return {
    ok: false,
    mensagem: mensagem?.trim()
      ? mensagem
      : "Não foi possível concluir agora. Tente de novo em instantes.",
  };
}

export async function listarOcupacoes(): Promise<Ocupacao[] | null> {
  const { data, error } = await supabase.rpc("mei_ocupacoes_lista");
  if (error) return null;
  return (data ?? []) as Ocupacao[];
}

/**
 * Tira acento e caixa para comparar. A lista do legado veio em CAIXA ALTA nas
 * vedadas e em Title Case nas permitidas, com acento nos dois — procurar
 * "acougue" tem de achar "Açougue", e procurar "acai" tem de achar "Açaí".
 */
export function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * O filtro da busca: casa no nome da atividade, na descrição da subclasse e no
 * CNAE. O CNAE entra porque o contador costuma chegar com o código na mão, e
 * comparar só dígitos faz "4789099" achar "4789-0/99" — a máscara do código
 * não pode atrapalhar quem digita do jeito que o cliente mandou.
 */
export function filtrar(lista: Ocupacao[], termo: string): Ocupacao[] {
  const t = normalizar(termo);
  if (t === "") return lista;
  const digitos = t.replace(/\D/g, "");
  return lista.filter((o) => {
    if (normalizar(o.atividade).includes(t)) return true;
    if (o.descricao && normalizar(o.descricao).includes(t)) return true;
    if (o.cnae) {
      if (normalizar(o.cnae).includes(t)) return true;
      if (digitos.length >= 3 && o.cnae.replace(/\D/g, "").includes(digitos)) return true;
    }
    return false;
  });
}

// ---------------------------------------------------------------------------
// O lado do admin
// ---------------------------------------------------------------------------

export type OcupacaoEditavel = {
  id?: string | null;
  vedada: boolean;
  atividade: string;
  cnae: string | null;
  descricao: string | null;
  icms: boolean | null;
  iss: boolean | null;
  exclusao: string | null;
};

export async function salvarOcupacao(o: OcupacaoEditavel): Promise<Escrita<string>> {
  const { data, error } = await supabase.rpc("mei_ocupacao_salvar", {
    p_id: o.id ?? null,
    p_vedada: o.vedada,
    p_atividade: o.atividade,
    p_cnae: o.cnae,
    p_descricao: o.descricao,
    p_icms: o.icms,
    p_iss: o.iss,
    // Norma de exclusão só existe em atividade vedada — a RPC recusa o
    // contrário, e mandar o resíduo de um formulário que trocou de lado seria
    // colher a recusa por um campo que a tela nem mostra mais.
    p_exclusao: o.vedada ? o.exclusao : null,
  });
  if (error) return falha(error.message);
  return { ok: true, res: data as string };
}

export async function excluirOcupacao(id: string): Promise<Escrita<null>> {
  const { error } = await supabase.rpc("mei_ocupacao_excluir", { p_id: id });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}
